// src/utils/czechHolidays.js

/* ======================================================
   Czech holidays utility
   ------------------------------------------------------
   Co řeší:
   - všechny české státní svátky pro libovolný rok
   - pohyblivé Velikonoce
   - rozlišení svátků, kdy jsou velké obchody zavřené
   - helpery pro planner / stats / kalendář
   ====================================================== */

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatISODate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/* ======================================================
   Velikonoce
   ====================================================== */
export function getEasterSunday(year) {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);

  return new Date(year, month - 1, day);
}

/* ======================================================
   Hlavní generátor svátků
   ====================================================== */
export function getCzechHolidays(year) {
  const easterSunday = getEasterSunday(year);

  const goodFriday = new Date(easterSunday);
  goodFriday.setDate(easterSunday.getDate() - 2);

  const easterMonday = new Date(easterSunday);
  easterMonday.setDate(easterSunday.getDate() + 1);

  const items = [
    {
      iso: `${year}-01-01`,
      name: "Den obnovy samostatného českého státu / Nový rok",
      code: "new-year",
      date: new Date(year, 0, 1),
      shopClosure: "full", // velké obchody zavřeno celý den
    },
    {
      iso: formatISODate(goodFriday),
      name: "Velký pátek",
      code: "good-friday",
      date: goodFriday,
      shopClosure: "none",
    },
    {
      iso: formatISODate(easterMonday),
      name: "Velikonoční pondělí",
      code: "easter-monday",
      date: easterMonday,
      shopClosure: "full",
    },
    {
      iso: `${year}-05-01`,
      name: "Svátek práce",
      code: "labour-day",
      date: new Date(year, 4, 1),
      shopClosure: "none",
    },
    {
      iso: `${year}-05-08`,
      name: "Den vítězství",
      code: "victory-day",
      date: new Date(year, 4, 8),
      shopClosure: "full",
    },
    {
      iso: `${year}-07-05`,
      name: "Den slovanských věrozvěstů Cyrila a Metoděje",
      code: "cyril-methodius",
      date: new Date(year, 6, 5),
      shopClosure: "none",
    },
    {
      iso: `${year}-07-06`,
      name: "Den upálení mistra Jana Husa",
      code: "jan-hus",
      date: new Date(year, 6, 6),
      shopClosure: "none",
    },
    {
      iso: `${year}-09-28`,
      name: "Den české státnosti",
      code: "czech-statehood",
      date: new Date(year, 8, 28),
      shopClosure: "full",
    },
    {
      iso: `${year}-10-28`,
      name: "Den vzniku samostatného československého státu",
      code: "independence-day",
      date: new Date(year, 9, 28),
      shopClosure: "full",
    },
    {
      iso: `${year}-11-17`,
      name: "Den boje za svobodu a demokracii",
      code: "freedom-democracy",
      date: new Date(year, 10, 17),
      shopClosure: "none",
    },
    {
      iso: `${year}-12-24`,
      name: "Štědrý den",
      code: "christmas-eve",
      date: new Date(year, 11, 24),
      shopClosure: "after12", // velké obchody zavřeno od 12:00
    },
    {
      iso: `${year}-12-25`,
      name: "1. svátek vánoční",
      code: "christmas-day",
      date: new Date(year, 11, 25),
      shopClosure: "full",
    },
    {
      iso: `${year}-12-26`,
      name: "2. svátek vánoční",
      code: "st-stephen",
      date: new Date(year, 11, 26),
      shopClosure: "full",
    },
  ];

  const byIso = Object.fromEntries(items.map((item) => [item.iso, item]));

  return {
    year,
    items,
    byIso,
    holidaySet: new Set(items.map((item) => item.iso)),
    closedShopSet: new Set(
      items.filter((item) => item.shopClosure === "full").map((item) => item.iso)
    ),
    partialClosureSet: new Set(
      items.filter((item) => item.shopClosure === "after12").map((item) => item.iso)
    ),
  };
}

/* ======================================================
   Jednoduché helpery
   ====================================================== */

export function getHolidayInfo(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return null;
  const [yearStr] = isoDate.split("-");
  const year = Number(yearStr);
  if (!Number.isInteger(year)) return null;

  const holidays = getCzechHolidays(year);
  return holidays.byIso[isoDate] || null;
}

export function isCzechHoliday(isoDate) {
  return !!getHolidayInfo(isoDate);
}

export function isBigShopClosedHoliday(isoDate) {
  const info = getHolidayInfo(isoDate);
  return info?.shopClosure === "full";
}

export function isBigShopPartialClosureHoliday(isoDate) {
  const info = getHolidayInfo(isoDate);
  return info?.shopClosure === "after12";
}

export function isBigShopOpenHoliday(isoDate) {
  const info = getHolidayInfo(isoDate);
  if (!info) return false;
  return info.shopClosure === "none";
}

/* ======================================================
   Praktický helper pro vaši aplikaci
   ------------------------------------------------------
   Vrací provozní info pro svátek:
   - běžná lékárna může mít vlastní pravidlo
   - mall může mít vlastní override
   - utilita sama říká jen to, co je v ČR "retail-closed"
   ====================================================== */

export function getHolidayBusinessInfo(isoDate) {
  const info = getHolidayInfo(isoDate);

  if (!info) {
    return {
      isHoliday: false,
      name: null,
      code: null,
      shopClosure: "none",
      isBigShopClosed: false,
      isBigShopPartialClosure: false,
      isBigShopOpen: false,
    };
  }

  return {
    isHoliday: true,
    name: info.name,
    code: info.code,
    shopClosure: info.shopClosure, // "none" | "full" | "after12"
    isBigShopClosed: info.shopClosure === "full",
    isBigShopPartialClosure: info.shopClosure === "after12",
    isBigShopOpen: info.shopClosure === "none",
  };
}